import SimpleTooltip from "@rio-cloud/rio-uikit/SimpleTooltip"
import Table, {
    TableBody,
    TableColumn,
    TableHeader,
    TableHeaderColumn,
    TableHeaderColumnProps,
    TableRow
} from "@rio-cloud/rio-uikit/Table"
import { FormattedDate, FormattedMessage, useIntl } from "react-intl"
import {
    Address,
    BulkFreight,
    CargoFreight,
    HandlingUnit,
    Shipment,
} from "@/model/transportorder/transportOrder"
import { hasDangerousGoods } from "@/model/transportorder/transportOrderDangerousGoods"
import { InboundSortDirection, InboundSortField } from "./inboundTransportOrderUrlState"
import { PropsWithChildren } from "react"
import { getTimezoneOfCountry } from "@/timezones/timezones"

const DateCell = ({ value, country }: { value?: string, country?: string }) => {
    if (!value) {
        return <span>–</span>
    }

    const timezone = getTimezoneOfCountry(country)

    return <span className="white-space-no-wrap">
        <FormattedDate value={value} year="numeric" month="2-digit" day="2-digit" timeZone={timezone}/>
    </span>
}

const TextWithTooltip = ({ value }: { value?: string }) =>
    value ?
        <SimpleTooltip content={value} placement={"left"}>
            <span>
                {value}
            </span>
        </SimpleTooltip> 
        : <></>

const PartyCell = ({ name, duns, supplierCode, address }: { name?: string, duns?: string, supplierCode?: string, address?: Address }) => (
    <div className="display-flex flex-column gap-5">
        <span className="text-bold">{name}</span>
        {
            address && <div>
                <div>
                    {address.street}
                </div>
                <div>
                    {address.postal_code} {address.city} {address.country}
                </div>
            </div>
        }
        <div>
            <div className="ellipsis-1 text-color-dark text-size-12">
                <FormattedMessage id={"inboundTransportOrders.party.duns"} values={{ duns: duns ?? "–" }}/>
            </div>
            <div className="ellipsis-1 text-color-dark text-size-12">
                <FormattedMessage
                    id={"inboundTransportOrders.party.supplierCode"}
                    values={{ supplierCode: supplierCode ?? "–" }}
                />
            </div>
        </div>
    </div>
)

const CargoHandlingUnit = ({ handlingUnit }: { handlingUnit: HandlingUnit }) => (
    <div className="display-flex align-items-center gap-5">
        <span className="rioglyph rioglyph-parcel" aria-hidden="true"/>
        <span>
            {handlingUnit.quantity === undefined
                ? "–"
                : <FormattedMessage
                    id="inboundTransportOrders.unit.pieces"
                    values={{ quantity: handlingUnit.quantity }}
                />
            } <span className={"text-medium"}>{handlingUnit.packaging_material_id ?? "–"}</span>
        </span>
    </div>
)

const ShipmentContentsCell = ({ shipment }: { shipment: Shipment }) => {
    const freight = shipment.freight
    if (freight.type === "BULK_DRY" || freight.type === "BULK_LIQUID") {
        return (
            <div className="display-flex align-items-center gap-5">
                <span className="rioglyph rioglyph-cargo" aria-hidden="true"/>
                <span>{(freight as BulkFreight).article_id ?? "–"}</span>
            </div>
        )
    }

    const handlingUnits = (freight as CargoFreight).handling_units
    return (
        <div className="display-flex flex-column gap-5">
            {handlingUnits.length === 0 && <span>–</span>}
            {handlingUnits.map((handlingUnit, index) =>
                <CargoHandlingUnit key={index} handlingUnit={handlingUnit}/>
            )}
        </div>
    )
}

const DangerousGoodsCell = ({ shipment }: { shipment: Shipment }) => {
    const intl = useIntl()
    if (!hasDangerousGoods([shipment])) {
        return null
    }
    const label = intl.formatMessage({ id: "inboundTransportOrders.status.dangerousGoods" })
    return (
        <SimpleTooltip content={label}>
            <span className="badge badge-secondary" aria-label={label}>
                <span className="rioglyph rioglyph-dangerousgoods" aria-hidden="true"/>
            </span>
        </SimpleTooltip>
    )
}

const ConfirmationCell = ({ confirmed }: { confirmed: boolean }) => {
    const intl = useIntl()
    const labelId = confirmed
        ? "inboundTransportOrders.status.confirmed"
        : "inboundTransportOrders.status.notConfirmed"
    const label = intl.formatMessage({ id: labelId })
    return (
        <SimpleTooltip content={label}>
            <span
                className={`rioglyph rioglyph-ok-sign ${confirmed ? "text-color-success" : "text-color-light"} text-size-18`}
                aria-label={label}
            />
        </SimpleTooltip>
    )
}

const TransportOrderTableHeaderColumn = (props: PropsWithChildren<TableHeaderColumnProps<Shipment & Record<string, unknown>>>) => (
    <TableHeaderColumn {...props}>
        <span className={"text-wrap-balance"}>
            {props.children}
        </span>
    </TableHeaderColumn>
)

interface InboundTransportOrderTableProps {
    rows: Shipment[]
    sortBy: InboundSortField
    sortDirection: InboundSortDirection
    onSortChange: (sortBy: InboundSortField, sortDirection: InboundSortDirection) => void
}

export const InboundTransportOrderTable = (props: InboundTransportOrderTableProps) => (
    <Table<Shipment & Record<string, unknown>>
        rowKey="id"
        condensed
        border
        hover
        sortBy={props.sortBy}
        sortDirection={props.sortDirection}
        onSortChange={(columnKey, direction) =>
            props.onSortChange(columnKey as InboundSortField, direction)
        }
        noRowsState={
            <div className="padding-25 text-center text-color-muted">
                <FormattedMessage id="inboundTransportOrders.empty"/>
            </div>
        }
    >
        <TableHeader>
            <TransportOrderTableHeaderColumn columnKey={InboundSortField.DOCUMENT_DATE} width="7%" sortable>
                <FormattedMessage id="inboundTransportOrders.column.documentDate"/>
            </TransportOrderTableHeaderColumn>
            <TransportOrderTableHeaderColumn columnKey={InboundSortField.REQUESTED_PICKUP_DATE_AT_SHIP_FROM} width="7%" sortable>
                <FormattedMessage id="inboundTransportOrders.column.requestedPickupAtShipFrom"/>
            </TransportOrderTableHeaderColumn>
            <TransportOrderTableHeaderColumn columnKey={InboundSortField.ESTIMATED_ARRIVAL_DATE_OF_CURRENT_LEG} width="7%" sortable>
                <FormattedMessage id="inboundTransportOrders.column.estimatedArrivalOfCurrentLeg"/>
            </TransportOrderTableHeaderColumn>
            <TransportOrderTableHeaderColumn columnKey={InboundSortField.CALCULATED_ARRIVAL_DATE_AT_SHIP_TO} width="7%" sortable>
                <FormattedMessage id="inboundTransportOrders.column.calculatedArrivalAtShipTo"/>
            </TransportOrderTableHeaderColumn>
            <TransportOrderTableHeaderColumn columnKey={"transportOrderNumber"}>
                <FormattedMessage id="inboundTransportOrders.column.transportOrderNumber"/>
            </TransportOrderTableHeaderColumn>
            <TransportOrderTableHeaderColumn columnKey="shipFrom">
                <FormattedMessage id="inboundTransportOrders.column.shipFrom"/>
            </TransportOrderTableHeaderColumn>
            <TransportOrderTableHeaderColumn columnKey="shipTo">
                <FormattedMessage id="inboundTransportOrders.column.shipTo"/>
            </TransportOrderTableHeaderColumn>
            <TransportOrderTableHeaderColumn columnKey="freightForwarder">
                <FormattedMessage id="inboundTransportOrders.column.freightForwarder"/>
            </TransportOrderTableHeaderColumn>
            <TransportOrderTableHeaderColumn columnKey="shipmentContents">
                <FormattedMessage id="inboundTransportOrders.column.shipmentContents"/>
            </TransportOrderTableHeaderColumn>
            <TransportOrderTableHeaderColumn columnKey="transportTypes" width="7%">
                <FormattedMessage id="inboundTransportOrders.column.transportType"/>
            </TransportOrderTableHeaderColumn>
            <TransportOrderTableHeaderColumn columnKey="hasDangerousGoods" width="4%" horizontalAlign="center">
                <FormattedMessage id="inboundTransportOrders.column.dangerousGoods"/>
            </TransportOrderTableHeaderColumn>
            <TransportOrderTableHeaderColumn columnKey="confirmationStatus" width="5%" horizontalAlign="center">
                <FormattedMessage id="inboundTransportOrders.column.accepted"/>
            </TransportOrderTableHeaderColumn>
        </TableHeader>
        <TableBody>
            {props.rows.map(row => (
                <TableRow key={row.id} id={row.id}>
                    <TableColumn columnKey={InboundSortField.DOCUMENT_DATE} verticalAlign="middle">
                        <DateCell value={row.createdDate}/>
                    </TableColumn>
                    <TableColumn columnKey={InboundSortField.REQUESTED_PICKUP_DATE_AT_SHIP_FROM} verticalAlign="middle">
                        <DateCell
                            value={row.startOfTransportChain.pickupRequestedAt.time_from}
                            country={row.startOfTransportChain.consignor.address?.country}
                        />
                    </TableColumn>
                    <TableColumn columnKey={InboundSortField.ESTIMATED_ARRIVAL_DATE_OF_CURRENT_LEG} verticalAlign="middle">
                        <DateCell
                            value={row.currentTransportLeg.arrivalEstimatedAt?.time_from}
                            country={row.currentTransportLeg.placeOfArrival.address?.country}
                        />
                    </TableColumn>
                    <TableColumn columnKey={InboundSortField.CALCULATED_ARRIVAL_DATE_AT_SHIP_TO} verticalAlign="middle">
                        <DateCell
                            value={row.endOfTransportChain.deliveryEstimatedAt?.time_to}
                            country={row.endOfTransportChain.consignee.address?.country}
                        />
                    </TableColumn>
                    <TableColumn columnKey={"transportOrderNumber"} verticalAlign="middle">
                        <TextWithTooltip value={row.external_identification}/>
                    </TableColumn>
                    <TableColumn columnKey="shipFrom" verticalAlign="middle">
                        <PartyCell
                            name={row.startOfTransportChain.consignor.name}
                            duns={row.startOfTransportChain.consignor.externalIdentifiers.duns}
                            supplierCode={row.startOfTransportChain.consignor.externalIdentifiers.supplierCode}
                            address={row.startOfTransportChain.consignor.address}
                        />
                    </TableColumn>
                    <TableColumn columnKey="shipTo" verticalAlign="middle">
                        <PartyCell
                            name={row.endOfTransportChain.consignee.name}
                            duns={row.endOfTransportChain.consignee.externalIdentifiers.duns}
                            supplierCode={row.endOfTransportChain.consignee.externalIdentifiers.supplierCode}
                            address={row.endOfTransportChain.consignee.address}
                        />
                    </TableColumn>
                    <TableColumn columnKey="freightForwarder" verticalAlign="middle">
                        <PartyCell
                            name={row.freightForwarder.name}
                            duns={row.freightForwarder.duns}
                            supplierCode={row.freightForwarder.supplierCode}
                        />
                    </TableColumn>
                    <TableColumn columnKey="shipmentContents" verticalAlign="middle">
                        <ShipmentContentsCell shipment={row}/>
                    </TableColumn>
                    <TableColumn columnKey="transportType" verticalAlign="middle">
                        <span>{row.processIndicator} / {
                            row.freight.type === "CARGO_EMPTIES" ?
                                <FormattedMessage id={"shipment.type.empties"}/>
                                : <FormattedMessage id={"shipment.type.materials"}/>}
                        </span>
                    </TableColumn>
                    <TableColumn
                        columnKey="hasDangerousGoods"
                        verticalAlign="middle"
                        horizontalAlign="center"
                    >
                        <DangerousGoodsCell shipment={row}/>
                    </TableColumn>
                    <TableColumn
                        columnKey="confirmationStatus"
                        verticalAlign="middle"
                        horizontalAlign="center"
                    >
                        <ConfirmationCell confirmed={row.isConfirmed}/>
                    </TableColumn>
                </TableRow>
            ))}
        </TableBody>
    </Table>
)
